import { T } from "./i18n";
import { extractCity } from "@/lib/address";
import { prettyBot } from "@/lib/bots";

export const PILLAR_MAX = { web: 18, onpage: 20, local: 13, gbp: 20, reviews: 17, ai: 12 };

// Style lookups are language-independent (colors don't translate); only the
// LABEL TEXT varies by language, via T[lang].
export const TONE_BADGE = {
  positive: "bg-green-600 text-white",
  neutral: "bg-slate-200 text-slate-700",
  negative: "bg-red-600 text-white",
};

export const SEVERITY_CLS = {
  critical: "bg-red-50 text-red-700 border-red-200",
  high: "bg-orange-50 text-orange-700 border-orange-200",
  medium: "bg-amber-50 text-amber-700 border-amber-200",
  low: "bg-slate-50 text-slate-600 border-slate-200",
  // Only audit/link_structure.py/technical_seo.py emit this — a
  // confirmation, not a problem severity, but reusing the same label/style
  // lookup avoids a second rendering branch for one finding type.
  positive: "bg-green-50 text-green-700 border-green-200",
};

export const CWV_CLS = {
  good: "bg-green-50 text-green-700 border-green-200",
  ok: "bg-amber-50 text-amber-700 border-amber-200",
  poor: "bg-red-50 text-red-700 border-red-200",
};

// Tab structure (user request 2026-09-07): the report grew to ~20 stacked
// cards over several feature additions, which non-technical recipients
// experienced as clutter. Grouping the same cards into topic tabs — no
// data or logic changes — is the fix; see the Claude Design mockup this
// was validated against before implementation.
//
// TAB_ICONS values are set by the consumer (ReportPage.js) since lucide-react
// icon components are imported there; kept as a plain key list here plus a
// setter so this module has no JSX/icon-library dependency.
export const TAB_KEYS = ["overview", "visibility", "reputation", "legal", "plan"];

// Findings are pillar-tagged; each pillar's group renders inside whichever
// tab actually discusses that topic. `gbp` (Google-Unternehmensprofil
// completeness) belongs with the other visibility/profile signals, not with
// review-based reputation — see spec §4 (2026-09-13 fix; previously
// REPUTATION_PILLARS incorrectly included "gbp").
export const VISIBILITY_PILLARS = ["web", "onpage", "local", "ai", "gbp"];
export const REPUTATION_PILLARS = ["reviews"];

export const initials = (name) =>
  (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("") || "?";

export const scoreTone = (s) =>
  s <= 25 ? { bg: "hsl(0,72%,96%)", fg: "hsl(0,72%,38%)", bar: "hsl(0,72%,50%)" }
  : s <= 50 ? { bg: "hsl(28,85%,96%)", fg: "hsl(28,85%,34%)", bar: "hsl(28,85%,52%)" }
  : s <= 75 ? { bg: "hsl(45,90%,96%)", fg: "hsl(38,80%,32%)", bar: "hsl(45,90%,48%)" }
  : { bg: "hsl(142,50%,96%)", fg: "hsl(142,60%,28%)", bar: "hsl(142,55%,42%)" };

// Named bands so the raw number carries a plain-language verdict too — a
// non-technical reader shouldn't have to guess whether "58/100" is fine or
// alarming (user request 2026-09-07, based on a ChatGPT-authored report
// spec the user shared).
export const scoreBand = (s, lang) => {
  const bands = T[lang].scoreBands;
  return bands.find((b) => s <= b.max) || bands[bands.length - 1];
};

// Google's published Core Web Vitals thresholds (good / needs improvement /
// poor) — used to label the raw ms/unitless numbers PageSpeed Insights
// returns, since "2 480 ms" means nothing to a non-technical reader without it.
export const cwvTone = (value, [good, ok]) =>
  value == null ? null : value <= good ? "good" : value <= ok ? "ok" : "poor";

// Per-review "Aussagekraft" badge (1-5): how many local-SEO-relevant
// details (Ort/Marke/Leistung/Ausführlichkeit) a showcase review carries.
export const qualityTone = (q) =>
  q <= 2 ? { bg: "hsl(0,72%,94%)", fg: "hsl(0,72%,38%)" }
  : q === 3 ? { bg: "hsl(38,90%,92%)", fg: "hsl(32,80%,32%)" }
  : { bg: "hsl(142,50%,92%)", fg: "hsl(142,60%,26%)" };

// The summary report deliberately withholds individual findings per pillar
// (they only appear once the paid wide report is approved — see
// memory/PRD.md §14) and shows this curiosity-inducing tally instead: how
// many of the checked criteria passed, phrased to make the gap concrete
// without listing what it actually is. Both helpers now live in T[lang]
// (daysAgo/checklistTeaser) — kept as thin named wrappers here so call
// sites read the same as before, just with a `lang` argument threaded in.
export const daysAgoLabel = (days, lang) => T[lang].daysAgo(days);
export const checklistTeaser = (label, passed, total, lang) => T[lang].checklistTeaser(label, passed, total);

// Wraps occurrences of the business's own name (green) and known
// competitor names — data.top3[].label, real businesses from the same
// search cohort, not guessed — (amber) inside a raw AI answer excerpt, so
// a reader can see at a glance who the AI actually recommended instead.
// Longest names first so "Kfz-Gutachter Müller GmbH" doesn't get shadowed
// by a shorter partial match.
//
// Returns plain marker objects (`{ __mark: true, key, cls, text }`) instead
// of JSX — this file stays JSX-free (plain .js, testable with plain Jest, no
// React render needed). The call site renders those marker objects as
// `<mark>` elements.
export function highlightNames(text, businessName, competitorNames) {
  if (!text) return text;
  const marks = [
    ...(businessName ? [{ name: businessName, cls: "bg-green-200 text-green-900 rounded px-0.5 font-semibold" }] : []),
    ...(competitorNames || [])
      .filter((n) => n && n.trim().length > 1)
      .map((n) => ({ name: n, cls: "bg-amber-200 text-amber-900 rounded px-0.5 font-semibold" })),
  ].sort((a, b) => b.name.length - a.name.length);
  if (!marks.length) return text;
  const escaped = marks.map((m) => m.name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const re = new RegExp(`(${escaped.join("|")})`, "gi");
  return text.split(re).map((part, i) => {
    const match = marks.find((m) => m.name.toLowerCase() === part.toLowerCase());
    return match ? { __mark: true, key: i, cls: match.cls, text: part } : part;
  });
}

// --- Free-report (SummaryReport) logic — pure, so it is testable without a DOM ---

// Same boundaries as T[lang].scoreBands so the verdict sentence and the band
// label under the score can never disagree.
export const verdictKey = (score) =>
  typeof score !== "number" || Number.isNaN(score) ? null :
  score <= 39 ? "critical" : score <= 59 ? "weak" : score <= 74 ? "mid" : score <= 89 ? "strong" : "top";

const MAX_HOOKS = 3;

// The three things "we noticed straight away", each built ONLY from data the
// free report already carries — so every one can be checked by the reader in
// seconds. Priority is the order of the pushes below. Returns descriptors, not
// text: SummaryReport looks the wording up in summaryCopy.js by `id`.
export function pickTopHooks(data, skipIds = []) {
  const d = data || {};
  const facts = d.free_facts || {};
  const hooks = [];

  if (facts.has_website === false) {
    hooks.push({ id: "noWebsite", args: [extractCity(d.address)], checkArgs: [] });
  }

  const mr = d.maps_rank;
  if (mr && !mr.error && mr.total_checked && mr.keyword) {
    if (mr.found === false || !mr.rank_absolute) {
      hooks.push({ id: "mapsMissing", args: [mr.total_checked, mr.keyword], checkArgs: [mr.keyword] });
    } else if (mr.rank_absolute > 3) {
      // Rank 1-3 is good news; presenting it as a shortcoming would be untrue.
      hooks.push({ id: "mapsRank", args: [mr.rank_absolute, mr.total_checked, mr.keyword], checkArgs: [mr.keyword] });
    }
  }

  const bots = facts.ai_bots_blocked || [];
  if (bots.length && facts.domain) {
    hooks.push({ id: "aiBlocked", args: [bots.map(prettyBot).join(", "), facts.domain], checkArgs: [facts.domain] });
  }

  const cit = d.citations;
  if (cit && !cit.error && cit.total_count > 0 && cit.found_count < cit.total_count) {
    hooks.push({ id: "citations", args: [cit.found_count, cit.total_count, cit.total_count - cit.found_count], checkArgs: [] });
  }

  const nap = d.nap_check;
  if (nap && nap.checked > 0 && nap.consistent < nap.checked) {
    hooks.push({ id: "nap", args: [nap.checked - nap.consistent, nap.checked], checkArgs: [] });
  }

  const newest = d.review_analysis?.newest_days_old;
  if (typeof newest === "number" && newest > 90) {
    hooks.push({ id: "reviewRecency", args: [newest], checkArgs: [] });
  }

  return hooks.filter((h) => !skipIds.includes(h.id)).slice(0, MAX_HOOKS);
}

// Which of the top-3 competitors a raw AI answer actually names — the live AI
// test's "Genannt werden stattdessen: …" line. Real names from the same search
// cohort, matched in the answer text itself, never guessed.
export function competitorsInText(text, top3) {
  if (!text) return [];
  const t = text.toLowerCase();
  return (top3 || [])
    .map((c) => c.label)
    .filter((l) => l && l.trim().length > 1 && t.includes(l.toLowerCase()));
}

// "Gemessen am …" — a measurement without a date is a claim, not a measurement.
export const formatMeasuredOn = (iso, lang) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString(lang === "en" ? "en-GB" : "de-DE", { day: "2-digit", month: "long", year: "numeric" });
};

// "Was Sie bereits gut machen": passed checks grouped per pillar in display
// order; only pillars with a passed check appear. allPassed = every measured
// check of that pillar passed (checks[pillar] = {passed, total}).
export function groupPassedByPillar(passed, pillarKeys, checks) {
  const list = Array.isArray(passed) ? passed : [];
  return (pillarKeys || [])
    .map((pillar) => {
      const titles = list.filter((p) => p && p.pillar === pillar && p.title).map((p) => p.title);
      const c = (checks || {})[pillar];
      return { pillar, titles, allPassed: !!c && c.total > 0 && c.passed === c.total };
    })
    .filter((g) => g.titles.length > 0);
}

// PassedChecks.js flattens the same per-pillar groups into one flat list
// (lovable.dev mockup idea #3: drop the pillar sub-headings and the
// "Alles bestanden" pillar badge, show every passed-check title as a single
// checklist) — no dedupe, just concatenation in groups' existing order.
export function flattenPassedTitles(groups) {
  return (groups || []).flatMap((g) => g.titles || []);
}

// Where "see all points" buttons scroll: the locked findings when that block is
// rendered, otherwise the offer (always on the page). `doc` is injectable for tests.
export function lockedScrollTarget(doc = typeof document !== "undefined" ? document : null) {
  if (!doc) return null;
  return doc.getElementById("locked") || doc.getElementById("angebot");
}

// Same worst-first order LockedFindings.js already uses for its own local
// SEVERITY_ORDER — kept as a second copy here rather than exported/shared
// because the two call sites want it for different purposes (grouping vs.
// this flat sort) and neither is the other's dependency.
const ACTION_SUMMARY_SEVERITY_ORDER = ["critical", "high", "medium", "low"];

// A short "what to do" synthesis for the end of one report section (Google
// Maps, AI providers, ...): the ALREADY-EXISTING `.fix` text of that
// section's own findings, worst-first, deduped, capped at `max`. Adds no new
// claims or numbers — every bullet is text the backend already sent for a
// finding shown elsewhere on the same page (see audit_findings in
// server.py). Returns null when there is nothing to show (no findings for
// these pillars, or none of them carry a `.fix`), so the caller can render
// nothing rather than an empty box.
export function sectionActionSummary(findings, pillarKeys, max = 4) {
  const keys = pillarKeys || [];
  const relevant = (Array.isArray(findings) ? findings : [])
    .filter((f) => f && keys.includes(f.pillar) && f.fix);
  if (!relevant.length) return null;

  const severityIndex = (f) => {
    const i = ACTION_SUMMARY_SEVERITY_ORDER.indexOf(f.severity);
    return i < 0 ? ACTION_SUMMARY_SEVERITY_ORDER.length : i;
  };
  const sorted = relevant
    .map((f, i) => ({ f, i })) // stable sort: keep original order within a severity tier
    .sort((a, b) => severityIndex(a.f) - severityIndex(b.f) || a.i - b.i)
    .map(({ f }) => f);

  const seen = new Set();
  const fixes = [];
  for (const f of sorted) {
    if (seen.has(f.fix)) continue;
    seen.add(f.fix);
    fixes.push(f.fix);
    if (fixes.length >= max) break;
  }
  return fixes.length ? { fixes } : null;
}
